import type { ComponentType, CSSProperties } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@repo/ui/components/ui/avatar";
import distributionListSrc from "./assets/avatar-distribution-list.svg";
import escalationSrc from "./assets/avatar-escalation.svg";
import teamUntaggedSrc from "./assets/avatar-team-untagged.svg";
import {
  entityAvatarFallback,
  entityAvatarGlyph,
  entityAvatarRoot,
  entityAvatarStyles as s,
} from "./EntityAvatar.styles";
import type {
  AvatarFallback as AvatarFallbackSpec,
  AvatarGlyph,
  AvatarImageGlyph,
  EntityAvatarIconProps,
  EntityAvatarProps,
} from "./EntityAvatar.types";
import {
  ActivatedTeamGlyph,
  ForumGlyph,
  GroupGlyph,
  PatientCareGlyph,
  RoleGlyph,
  UserGlyph,
} from "./EntityAvatarIcons";
import { resolveEntityAvatar } from "./entityAvatarVisual";

const GLYPHS: Record<AvatarGlyph, ComponentType<EntityAvatarIconProps>> = {
  user: UserGlyph,
  group: GroupGlyph,
  forum: ForumGlyph,
  activatedTeam: ActivatedTeamGlyph,
  patientCare: PatientCareGlyph,
  role: RoleGlyph,
};

const IMAGE_GLYPHS: Record<AvatarImageGlyph, string> = {
  distributionList: distributionListSrc,
  escalation: escalationSrc,
  teamUntagged: teamUntaggedSrc,
};

function FallbackContent({ fallback }: { fallback: AvatarFallbackSpec }) {
  if (fallback.kind === "initials") {
    return <span className={s.initials}>{fallback.text}</span>;
  }
  if (fallback.kind === "image") {
    return <img src={IMAGE_GLYPHS[fallback.glyph]} alt="" className={s.imageGlyph} />;
  }
  const Glyph = GLYPHS[fallback.glyph];
  return <Glyph className={entityAvatarGlyph({ glyph: fallback.glyph })} />;
}

/** The avatar TigerConnect shows for a conversation's counterparty. */
export function EntityAvatar(props: EntityAvatarProps) {
  const { shape, photo, fallback } = resolveEntityAvatar(props);
  const tone = fallback.kind === "image" ? "none" : fallback.tone;
  // Tag colors are per-entity data, so they can't be a design token.
  const style: CSSProperties | undefined =
    fallback.kind !== "image" && fallback.tagColor
      ? { backgroundColor: fallback.tagColor }
      : undefined;

  return (
    <Avatar className={entityAvatarRoot({ shape })}>
      {photo && <AvatarImage src={photo} alt="" className={s.photo} />}
      <AvatarFallback className={entityAvatarFallback({ shape, tone })} style={style}>
        <FallbackContent fallback={fallback} />
      </AvatarFallback>
    </Avatar>
  );
}
